import { useEffect, useState } from "react";
import client from "../lib/api";
import { CalendarX, Trash, Plus } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";

const fmt = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

export default function BookingBlackouts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ start: "", end: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await client.get("/booking/blackouts"); setItems(data.blackouts || []); }
    catch { toast.error("Couldn't load blackout dates."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.start) { toast.error("Pick a start date."); return; }
    setSaving(true);
    try {
      await client.post("/booking/blackouts", { start: form.start, end: form.end || null, reason: form.reason || null });
      toast.success("Blackout added. Those days won't show as open.");
      setForm({ start: "", end: "", reason: "" });
      load();
    } catch { toast.error("Couldn't save the blackout."); }
    setSaving(false);
  };

  const remove = async (id) => {
    try { await client.delete(`/booking/blackouts/${id}`); setItems((x) => x.filter((b) => b.id !== id)); toast.success("Blackout removed."); }
    catch { toast.error("Couldn't remove."); }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="blackouts-panel">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#B08D57] font-semibold mb-1">Booking</p>
        <h2 className="text-2xl font-bold text-[#1A1715]">Availability &amp; Blackout Dates</h2>
        <p className="text-sm text-[#1A1715]/60 mt-1">Block holidays &amp; PTO so those days never appear as open on the public booking page.</p>
      </div>

      <div className="bg-white border border-[#1A1715]/12 rounded-lg p-5">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs uppercase tracking-wide text-[#1A1715]/60">Start date</Label>
            <Input type="date" data-testid="blackout-start" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-[#1A1715]/60">End date (optional)</Label>
            <Input type="date" data-testid="blackout-end" value={form.end} min={form.start} onChange={(e) => setForm({ ...form, end: e.target.value })} />
          </div>
          <div className="sm:col-span-1">
            <Label className="text-xs uppercase tracking-wide text-[#1A1715]/60">Reason</Label>
            <Input placeholder="Holiday, PTO&hellip;" data-testid="blackout-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>
          <Button data-testid="blackout-add" onClick={add} disabled={saving}
            className="bg-[#7E1119] hover:bg-[#5f0d13] text-white">
            <Plus size={16} weight="bold" className="mr-1" />{saving ? "Saving\u2026" : "Block dates"}
          </Button>
        </div>
        <p className="text-xs text-[#1A1715]/50 mt-3">Leave the end date empty to block a single day. Ranges are inclusive.</p>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-[#1A1715] mb-3">Blocked periods</h3>
        {loading ? (
          <p className="text-sm text-[#1A1715]/50">Loading&hellip;</p>
        ) : items.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-[#1A1715]/15 rounded-lg" data-testid="blackouts-empty">
            <CalendarX size={28} className="mx-auto text-[#1A1715]/30" />
            <p className="text-sm text-[#1A1715]/50 mt-2">No blackout dates yet. Every future day is bookable.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="blackouts-list">
            {items.map((b) => (
              <div key={b.id} data-testid={`blackout-row-${b.id}`}
                className="flex items-center gap-4 bg-white border border-[#1A1715]/12 rounded-lg px-4 py-3">
                <CalendarX size={20} className="text-[#7E1119] flex-none" weight="fill" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1A1715] text-sm">
                    {b.start === b.end ? fmt(b.start) : `${fmt(b.start)} \u2192 ${fmt(b.end)}`}
                  </p>
                  {b.reason && <p className="text-xs text-[#1A1715]/55">{b.reason}</p>}
                </div>
                <button data-testid={`blackout-del-${b.id}`} onClick={() => remove(b.id)}
                  className="text-[#1A1715]/40 hover:text-[#7E1119] transition-colors flex-none" aria-label="Remove blackout">
                  <Trash size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
